import type { SearchParameters } from '../searchParameters';
import type { SearchProfile } from '../searchProfile';
import type {
  AppliedFilterSiteExclusions,
  RawAppliedFilter,
} from './appliedFilter';
import { getMergedRawAppliedFilters, getRawAppliedFilters } from './derive';

/**
 * The removable chips for a search, for one site.
 *
 * The exclusions come from site config and do not change while the app runs,
 * so they are held once rather than passed to every call — the same reason
 * SearchParameterAvailability holds its own.
 *
 * `raw` is before the collection and video-duration merges, `merged` after.
 * Both are `raw` in the other sense: they carry names and values, never
 * display labels, which the presentation layer derives.
 */
export class AppliedFilters {
  readonly #siteExclusions: AppliedFilterSiteExclusions;

  constructor(siteExclusions: AppliedFilterSiteExclusions) {
    this.#siteExclusions = siteExclusions;
  }

  /** One entry per set parameter value, minus the ones that should not surface. */
  raw(
    searchParameters: SearchParameters,
    searchProfile: SearchProfile
  ): RawAppliedFilter[] {
    return getRawAppliedFilters(
      searchParameters,
      searchProfile,
      this.#siteExclusions
    );
  }

  /** As `raw`, with collections counted as one and a duration range as one. */
  merged(
    searchParameters: SearchParameters,
    searchProfile: SearchProfile
  ): RawAppliedFilter[] {
    return getMergedRawAppliedFilters(
      searchParameters,
      searchProfile,
      this.#siteExclusions
    );
  }
}
